import React from 'react';
import MDBox from 'src/components/md/MDBox';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import MDButton from 'src/components/md/MDButton';
import MDTypography from 'src/components/md/MDTypography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from 'src/md-examples/Tables/DataTable';
import { Column } from 'react-table';
import useWarehouses, {
  WarehouseDTO,
} from 'src/api/inventory-management/useWarehouses';

const WarehousesCard: React.FC<{
  openCreateModal: () => void;
  closeCreateModal: (refetch?: boolean) => void;
}> = ({ openCreateModal }) => {
  const warehouses = useWarehouses();

  const loading = warehouses.isLoading;
  const error = warehouses.error;

  const columns: Column<WarehouseDTO>[] = [
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ value }: { value: string | null | undefined }) => (
        <MDTypography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '300px',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
          }}
        >
          {value || '-'}
        </MDTypography>
      ),
    },
    {
      Header: 'Location',
      accessor: 'address',
      Cell: ({ value }: { value: WarehouseDTO['address'] }) => {
        if (!value) return <MDTypography>-</MDTypography>;

        const fullAddress = [
          value.addressLine1,
          value.addressLine2,
          value.locality,
          value.administrativeArea,
          value.postalCode,
          value.countryIso31661Alpha2,
        ]
          .filter(Boolean)
          .join(', ');

        const shortAddress = `${value.locality}${
          value.administrativeArea ? `, ${value.administrativeArea}` : ''
        }`;

        return (
          <Tooltip title={fullAddress} arrow placement="top">
            <MDTypography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '300px',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              {shortAddress}
            </MDTypography>
          </Tooltip>
        );
      },
    },
    {
      Header: '',
      id: 'actions',
      // @ts-ignore
      align: 'right',
      Cell: ({ row }: { row: { original: WarehouseDTO } }) => (
        <MDBox
          display="flex"
          sx={{
            justifyContent: 'flex-end',
            textAlign: 'right !important',
            width: '100%',
          }}
        >
          <IconButton
            onClick={() => console.log(row.original)}
            aria-label="edit"
            size="small"
            sx={{ opacity: 0.7, color: 'grey.600', '&:hover': { opacity: 1 } }}
            disabled
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="delete"
            size="small"
            sx={{ opacity: 0.7, color: 'grey.600', '&:hover': { opacity: 1 } }}
            disabled
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MDBox>
      ),
    },
  ];

  if (loading) {
    return (
      <MDBox pt={2} pb={3} px={3} display="flex" justifyContent="center">
        <CircularProgress />
      </MDBox>
    );
  } else if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <MDBox pt={2} pb={3} px={3}>
      {warehouses.data?.warehouses.length === 0 ? (
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={4}
        >
          <MDBox mb={2}>
            <MDBox mb={2}>
              <MDTypography variant="body2" color="text" sx={{ mt: 0, mb: 4 }}>
                {`It looks like there aren't any warehouses created yet.`}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => {
                openCreateModal();
              }}
            >
              Create
            </MDButton>
          </MDBox>
        </MDBox>
      ) : (
        <DataTable<WarehouseDTO>
          canSearch={false}
          table={{ columns, rows: warehouses.data?.warehouses ?? [] }}
          pagination={{ variant: 'gradient', color: 'info' }}
          showTotalEntries={true}
          isSorted={false}
        />
      )}
    </MDBox>
  );
};

export default WarehousesCard;
